/* eslint-disable sonarjs/cognitive-complexity */
import type { CartItemType } from '@/store/types/cart'
import type { GlobalStoreValues } from '@/store/useGlobalStore'
import useGlobalStore from '@/store/useGlobalStore'

import type { GtmAddToCartProps } from './events/cart/gtmAddToCart'
import { gtmAddToCart } from './events/cart/gtmAddToCart'
import type { GtmRemoveFromCartProps } from './events/cart/gtmRemoveFromCart'
import { gtmRemoveFromCart } from './events/cart/gtmRemoveFromCart'
import { gtmShareCart } from './events/cart/gtmShareCart'
import { gtmViewCart } from './events/cart/gtmViewCart'
import type { GtmCheckoutAccountDataAddedProps } from './events/checkout/gtmCheckoutAccountDataAdded'
import { gtmCheckoutAccountDataAdded } from './events/checkout/gtmCheckoutAccountDataAdded'
import type { GtmCheckoutPurchaseButtonClickedType } from './events/checkout/gtmCheckoutPurchaseButtonClicked'
import { gtmCheckoutPurchaseButtonClicked } from './events/checkout/gtmCheckoutPurchaseButtonClicked'
import type { GtmCheckoutPurchaseFrontendValidationErrorType } from './events/checkout/gtmCheckoutPurchaseFrontendValidationError'
import { gtmCheckoutPurchaseFrontendValidationError } from './events/checkout/gtmCheckoutPurchaseFrontendValidationError'
import type { GtmCheckoutShippingAddedType } from './events/checkout/gtmCheckoutShippingAdded'
import { gtmCheckoutShippingAdded } from './events/checkout/gtmCheckoutShippingAdded'
import type { GtmPurchaseCompleteType } from './events/checkout/gtmPurchaseComplete'
import { gtmPurchaseComplete } from './events/checkout/gtmPurchaseComplete'
import type { GtmPurchaseServerErrorType } from './events/checkout/gtmPurchaseServerError'
import { gtmPurchaseServerError } from './events/checkout/gtmPurchaseServerError'
import { gtmStartCheckout } from './events/checkout/gtmStartCheckout'
import type { GtmSelectItemProps } from './events/products/gtmSelectItem'
import { gtmSelectItem } from './events/products/gtmSelectItem'
import { gmtSetParameters } from './events/products/gtmSetParameters'
import type { GtmViewItemProps } from './events/products/gtmViewItem'
import { gtmViewItem } from './events/products/gtmViewItem'
import { gtmSignInClicked } from './events/signin/gtmSignInClicked'
import type { GtmSignInCompleted } from './events/signin/gtmSignInCompleted'
import { gtmSignInCompleted } from './events/signin/gtmSignInCompleted'
import type { GtmSignInServerError } from './events/signin/gtmSignInServerError'
import { gtmSignInServerError } from './events/signin/gtmSignInServerError'
import { gtmCreateAccountClicked } from './events/signup/gtmCreateAccountClicked'
import type { GtmCreateAccountValidationErrorType } from './events/signup/gtmCreateAccountValidationError'
import { gtmCreateAccountValidationError } from './events/signup/gtmCreateAccountValidationError'
import type { GtmCreateAccountServerError } from './events/signup/gtmCreateAccountServerError'
import { gtmCreateAccountServerError } from './events/signup/gtmCreateAccountServerError'
import type { GtmCreateAccountCompletedType } from './events/signup/gtmCreateAccountCompleted'
import { gtmCreateAccountCompleted } from './events/signup/gtmCreateAccountCompleted'
import { gtmLogout } from './events/signin/gtmLogout'
import type { GtmLanguageChangedType } from './events/navbar/gtmLanguageChanged'
import { gtmLanguageChanged } from './events/navbar/gtmLanguageChanged'
import {
  gtmProductDetailLinkCopied,
  type GtmProductsDetailLinkCopiedType,
} from './events/products/gtmProductDetailLinkCopied'
import type { GtmProductDetailDownloadPdfClickedType } from './events/products/gtmProductDetailDownloadPdfClicked'
import { gtmProductDetailDownloadPdfClicked } from './events/products/gtmProductDetailDownloadPdfClicked'
import type { GtmNavBarClickedType } from './events/navbar/gtmNavBarClicked'
import { gtmNavbarClicked } from './events/navbar/gtmNavBarClicked'
import type { GtmMarketChangedType } from './events/navbar/gtmMarketChanged'
import { gtmMarketChanged } from './events/navbar/gtmMarketChanged'
import type { GtmNavBarMenuItemClickedType } from './events/navbar/gtmNavbarMenuItemClicked'
import { gtmNavbarMenuItemClicked } from './events/navbar/gtmNavbarMenuItemClicked'
import type { GtmFooterLinksClickedType } from './events/footer/gtmFooterLinksClicked'
import { gtmFooterLinksClicked } from './events/footer/gtmFooterLinksClicked'
import { gtmNavbarSearchClicked } from './events/navbar/gtmNavbarSearchClicked'
import { gtmProfileMenuOpened } from './events/profile/gtmProfileMenuOpened'
import { gtmNavigationBarNavLinkClicked } from './events/navbar/gtmNavigationBarNavLinkClicked'
import {
  gtmUserReferralLinkCopied,
  type UserReferralLinkCopiedEventLocationsType,
} from './events/user/gtmUserReferralLinkCopied'
import {
  gtmUserUnicityIdCopied,
  type UserUnicityIdCopiedEventLocationsType,
} from './events/user/gtmUserUnicityIdCopied'
import { gtmUserReferralCodeCopied } from './events/user/gtmUserReferralCodeCopied'
import type { UserMenuNavigationClickedType } from './events/user/gtmUserMenuNavigationClicked'
import { gtmUserMenuNavigationClicked } from './events/user/gtmUserMenuNavigationClicked'

export type GtmGlobalStoreProps = {
  globalStore: GlobalStoreValues
}

export const useGTM = () => {
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID || ''

  const globalStore = useGlobalStore()

  const gtm = {
    marketChanged: (props: GtmMarketChangedType) => {
      gtmMarketChanged({ globalStore, ...props })
    },
    navbarClicked: (props: GtmNavBarClickedType) => {
      gtmNavbarClicked(props)
    },
    navbarMenuItemClicked: (props: GtmNavBarMenuItemClickedType) => {
      gtmNavbarMenuItemClicked(props)
    },
    navbarSearchClicked: () => {
      gtmNavbarSearchClicked()
    },
    languageChanged: (props: GtmLanguageChangedType) => {
      gtmLanguageChanged({ globalStore, ...props })
    },
    setParameters: () => gmtSetParameters({ globalStore }),
    viewItem: ({ product }: GtmViewItemProps) => {
      gtmViewItem({ globalStore, product })
    },
    selectItem: ({ product }: GtmSelectItemProps) => {
      gtmSelectItem({ globalStore, product })
    },
    addToCart: ({ cartItem, event_location }: GtmAddToCartProps) => {
      gtmAddToCart({ globalStore, cartItem, event_location })
    },
    removeFromCart: ({
      cartItem,
      qty,
      event_location,
    }: GtmRemoveFromCartProps) => {
      gtmRemoveFromCart({ globalStore, cartItem, qty, event_location })
    },
    shareURLCart: (cart: CartItemType[]) => {
      if (!cart) return
      gtmShareCart({ globalStore, cart })
    },
    viewCart: (cart: CartItemType[]) => {
      if (!cart) return
      gtmViewCart({ globalStore, cart })
    },
    startCheckout: (cart: CartItemType[]) => {
      if (!cart) return
      gtmStartCheckout({ globalStore, cart })
    },
    checkoutAccountDataAdded: ({
      scenario,
    }: GtmCheckoutAccountDataAddedProps) => {
      gtmCheckoutAccountDataAdded({ globalStore, scenario })
    },
    checkoutShippingAdded: (props: GtmCheckoutShippingAddedType) => {
      gtmCheckoutShippingAdded({
        globalStore,
        ...props,
      })
    },
    checkoutPurchaseButtonClicked: (
      props: GtmCheckoutPurchaseButtonClickedType,
    ) => {
      gtmCheckoutPurchaseButtonClicked({
        globalStore,
        ...props,
      })
    },
    checkoutPurchaseFrontendValidationError: (
      props: GtmCheckoutPurchaseFrontendValidationErrorType,
    ) => {
      gtmCheckoutPurchaseFrontendValidationError({
        globalStore,
        ...props,
      })
    },
    purchaseServerError: (props: GtmPurchaseServerErrorType) => {
      gtmPurchaseServerError({
        globalStore,
        ...props,
      })
    },
    purchaseComplete: (props: GtmPurchaseCompleteType) => {
      gtmPurchaseComplete({
        globalStore,
        ...props,
      })
    },
    signInClicked: () => {
      gtmSignInClicked()
    },
    signInServerError: ({ error }: GtmSignInServerError) => {
      gtmSignInServerError({ error })
    },
    signInCompleted: (props: GtmSignInCompleted) => {
      gtmSignInCompleted(props)
    },
    createAccountClicked: () => {
      gtmCreateAccountClicked()
    },
    createAccountValidationError: (
      props: GtmCreateAccountValidationErrorType,
    ) => {
      gtmCreateAccountValidationError(props)
    },
    createAccountServerError: (props: GtmCreateAccountServerError) => {
      gtmCreateAccountServerError(props)
    },
    createAccountCompleted: (props: GtmCreateAccountCompletedType) => {
      gtmCreateAccountCompleted(props)
    },
    logout: () => {
      gtmLogout()
    },
    productDetailLinkCopied: (props: GtmProductsDetailLinkCopiedType) => {
      gtmProductDetailLinkCopied({ globalStore, ...props })
    },
    productDetailDownloadPdfClicked: (
      props: GtmProductDetailDownloadPdfClickedType,
    ) => {
      gtmProductDetailDownloadPdfClicked({ globalStore, ...props })
    },
    footerLinksClicked: (props: GtmFooterLinksClickedType) => {
      gtmFooterLinksClicked(props)
    },
    navigationBarNavLinkClicked: (action: string) => {
      gtmNavigationBarNavLinkClicked(action)
    },
    profileMenuOpened: () => {
      gtmProfileMenuOpened()
    },
    userMenuNavigationClicked: (
      action: string,
      event_location: UserMenuNavigationClickedType,
    ) => {
      gtmUserMenuNavigationClicked(action, event_location)
    },
    userReferralLinkCopied: (
      event_location: UserReferralLinkCopiedEventLocationsType,
    ) => {
      gtmUserReferralLinkCopied(event_location)
    },
    userReferralCodeCopied: () => {
      gtmUserReferralCodeCopied()
    },
    userUnicityIdCopied: (
      event_location: UserUnicityIdCopiedEventLocationsType,
    ) => {
      gtmUserUnicityIdCopied(event_location)
    },
  }

  return { gtmId, gtm }
}
